import {
  Component,
  computed,
  inject,
  signal,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TranslocoModule } from '@jsverse/transloco';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, Users } from 'lucide-angular';
import { ButtonModule } from 'primeng/button';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import {
  PageHeaderAction,
  PageHeaderComponent,
} from 'src/app/shared/components/page-header/page-header.component';
import { CompanyClientService } from 'src/app/shared/services/company-client.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { CompanyClient } from 'src/app/trip-calculator/models/companyClients';
import { ClientCardComponent } from './components/client-card/client-card.component';
import { ClientFormDialogComponent } from './components/client-form-dialog/client-form-dialog.component';
import { TooltipIconComponent } from 'src/app/shared/tooltip-icon/tooltip-icon.component';

@Component({
  selector: 'app-company-and-client-list',
  templateUrl: './clients-page.component.html',
  standalone: true,
  imports: [
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    FormsModule,
    ConfirmDialogModule,
    TranslocoModule,
    LucideAngularModule,
    ButtonModule,
    PageHeaderComponent,
    ClientCardComponent,
    ClientFormDialogComponent,
    TooltipIconComponent,
  ],
})
export class ClientsPageComponent implements OnInit, OnDestroy {
  private translocoService = inject(TranslocoService);
  private notificationService = inject(NotificationService);
  private confirmationService = inject(ConfirmationService);
  private companyClientService = inject(CompanyClientService);
  private currentUserService = inject(CurrentUserService);

  readonly UsersIcon = Users;

  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  clients = this.companyClientService.companyClients;
  isLoading = this.companyClientService.isLoading;
  clientsError = this.companyClientService.error;

  searchQuery = signal('');
  debouncedSearchQuery = signal('');
  isSearching = signal(false);
  dialogVisible = signal(false);
  dialogMode = signal<'create' | 'edit'>('create');
  dialogLoading = signal(false);
  selectedClient = signal<CompanyClient | undefined>(undefined);

  filteredClients = computed(() => {
    const query = this.debouncedSearchQuery().toLowerCase().trim();
    const allClients = this.clients();
    if (!query) {
      return allClients;
    }
    return allClients.filter((c) => c.name.toLowerCase().includes(query));
  });

  headerActions: PageHeaderAction[] = [
    {
      label: 'accountMenu.organizationsManagement.addClient',
      icon: 'plus',
      variant: 'primary',
      onClick: () => this.openCreateDialog(),
      testId: 'add.client.button',
    },
  ];

  ngOnInit() {
    // Load clients from API (respects company context for super admins)
    this.companyClientService.loadClients();

    this.searchSubject$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        this.debouncedSearchQuery.set(query);
        this.isSearching.set(false);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(query: string) {
    this.searchQuery.set(query);
    if (query !== this.debouncedSearchQuery()) {
      this.isSearching.set(true);
    }
    this.searchSubject$.next(query);
  }

  openCreateDialog() {
    this.dialogMode.set('create');
    this.selectedClient.set(undefined);
    this.dialogVisible.set(true);
  }

  openEditDialog(client: CompanyClient) {
    this.dialogMode.set('edit');
    this.selectedClient.set(client);
    this.dialogVisible.set(true);
  }

  onSaveClient(data: { name: string; logo?: Blob }) {
    const clientExists = this.clients().some(
      (client) =>
        client.name.trim().toLowerCase() === data.name.toLowerCase() &&
        client.id !== this.selectedClient()?.id,
    );

    if (clientExists) {
      this.notificationService.error(
        this.translocoService.translate(
          'companyClientList.clientAlreadyExists',
        ),
      );
      return;
    }

    this.dialogLoading.set(true);

    if (this.dialogMode() === 'create') {
      this.createClient(data.name, data.logo);
    } else {
      this.updateClient(this.selectedClient()!.id, data.name, data.logo);
    }
  }

  private createClient(name: string, logo?: Blob) {
    this.companyClientService.createClient(name).subscribe({
      next: (client) => {
        this.uploadLogoThenFinish(
          client.id,
          logo,
          'companyClientList.clientCreatedSuccessfully',
        );
      },
      error: (error) => {
        this.dialogLoading.set(false);
        console.error('Failed to create client', error);
        this.notificationService.error(
          this.translocoService.translate('companyClientList.errorCreate'),
        );
      },
    });
  }

  private updateClient(clientId: string, newName: string, logo?: Blob) {
    this.companyClientService
      .updateClient(clientId, { name: newName })
      .subscribe({
        next: () => {
          this.uploadLogoThenFinish(
            clientId,
            logo,
            'companyClientList.clientUpdatedSuccessfully',
          );
        },
        error: (error) => {
          this.dialogLoading.set(false);
          console.error('Failed to update client', error);
          this.notificationService.error(
            this.translocoService.translate('companyClientList.errorUpdate'),
          );
        },
      });
  }

  /** Upload the cropped logo (if any) after the client is saved, then close the dialog. */
  private uploadLogoThenFinish(
    clientId: string,
    logo: Blob | undefined,
    successKey: string,
  ) {
    if (!logo) {
      this.dialogLoading.set(false);
      this.notificationService.success(
        this.translocoService.translate(successKey),
      );
      return;
    }

    this.companyClientService.uploadLogo(clientId, logo).subscribe({
      next: () => {
        this.dialogLoading.set(false);
        this.notificationService.success(
          this.translocoService.translate(successKey),
        );
      },
      error: (error) => {
        this.dialogLoading.set(false);
        console.error('Failed to upload client logo', error);
        this.notificationService.error(
          this.translocoService.translate('logoUpload.error'),
        );
      },
    });
  }

  retryLoadClients() {
    this.companyClientService.refreshClients();
  }

  confirmDelete(client: CompanyClient) {
    const message = this.translocoService.translate(
      'companyClientList.deleteClient',
    );
    const header = this.translocoService.translate(
      'companyClientList.deleteConfirmation',
    );
    this.confirmationService.confirm({
      message: message,
      header: header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translocoService.translate('yes'),
      rejectLabel: this.translocoService.translate('no'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.deleteClient(client.id),
    });
  }

  private deleteClient(clientId: string) {
    this.companyClientService.deleteClient(clientId).subscribe({
      next: () => {
        this.notificationService.success(
          this.translocoService.translate(
            'companyClientList.clientDeletedSuccessfully',
          ),
        );
      },
      error: (error) => {
        console.error('Failed to delete client', error);
        this.notificationService.error(
          this.translocoService.translate('companyClientList.errorDelete'),
        );
      },
    });
  }
}
